import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import axios from "axios";

// For receivable accounts: the user records a payment they sent to the
// company. It is saved as "pending" and only counts once the admin approves it.
export default function SubmitPaymentModal({ show, onHide, summary, banksUrl, submitUrl, onSuccess, t = (text) => text }) {
    const today = new Date().toISOString().split("T")[0];
    const [form, setForm] = useState({ amount: "", payment_date: today, bank_id: "", notes: "" });
    const [banks, setBanks] = useState([]);
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState("");
    const [saving, setSaving] = useState(false);

    const remaining = Number(summary?.remaining_amount || 0);
    const pending = Number(summary?.pending_amount || 0);
    const maxAmount = Math.max(remaining - pending, 0);
    const headers = { Authorization: `Bearer ${localStorage.getItem("authToken")}`, Accept: "application/json" };

    useEffect(() => {
        if (!show) return;
        setErrors({});
        setMessage("");
        setForm({ amount: maxAmount ? maxAmount.toFixed(2) : "", payment_date: today, bank_id: "", notes: "" });
        axios
            .get(banksUrl, { headers })
            .then((res) => setBanks(res.data?.data || []))
            .catch(() => setBanks([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const change = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: undefined }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrors({});
        setMessage("");

        try {
            const res = await axios.post(
                submitUrl,
                {
                    amount: form.amount,
                    payment_date: form.payment_date,
                    bank_id: form.bank_id || null,
                    notes: form.notes || null,
                },
                { headers }
            );
            onSuccess(res.data?.message || t("Payment sent for approval."));
            onHide();
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data?.errors || {});
                setMessage(err.response.data?.message || t("Please check the form."));
            } else {
                setMessage(err.response?.data?.message || t("Could not send the payment. Please try again."));
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldError = (name) => (Array.isArray(errors[name]) ? errors[name][0] : errors[name]);
    const fmt = (value) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <Modal show={show} onHide={onHide} centered>
            <Form onSubmit={submit} noValidate>
                <Modal.Header closeButton>
                    <Modal.Title className="h5">{t("Add a payment you sent")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted">
                        {t("Record money you sent to the company. Your balance updates after the admin approves it.")}
                    </p>
                    <div className="d-flex justify-content-between small border rounded-3 p-2 mb-3 bg-light">
                        <span>{t("You still owe")}: <strong>PKR {fmt(remaining)}</strong></span>
                        {pending > 0 && <span>{t("Waiting for approval")}: <strong>PKR {fmt(pending)}</strong></span>}
                    </div>
                    {message && !Object.keys(errors).length && <Alert variant="danger" className="py-2">{message}</Alert>}

                    <Form.Group className="mb-3" controlId="submit-payment-amount">
                        <Form.Label>{t("Amount (PKR)")}</Form.Label>
                        <Form.Control type="number" step="0.01" min="0" max={maxAmount || undefined} name="amount" value={form.amount} onChange={change} isInvalid={!!fieldError("amount")} required />
                        <Form.Control.Feedback type="invalid">{fieldError("amount")}</Form.Control.Feedback>
                        <Form.Text>{t("Up to")} PKR {fmt(maxAmount)}</Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="submit-payment-date">
                        <Form.Label>{t("Payment date")}</Form.Label>
                        <Form.Control type="date" name="payment_date" max={today} value={form.payment_date} onChange={change} isInvalid={!!fieldError("payment_date")} />
                        <Form.Control.Feedback type="invalid">{fieldError("payment_date")}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="submit-payment-bank">
                        <Form.Label>{t("Sent to company account")}</Form.Label>
                        <Form.Select name="bank_id" value={form.bank_id} onChange={change} isInvalid={!!fieldError("bank_id")}>
                            <option value="">{t("Select account (optional)")}</option>
                            {banks.map((bank) => (
                                <option key={bank.id} value={bank.id}>{bank.name}{bank.account_no ? ` (${bank.account_no})` : ""}</option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">{fieldError("bank_id")}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group controlId="submit-payment-notes">
                        <Form.Label>{t("Reference / notes")}</Form.Label>
                        <Form.Control as="textarea" rows={2} name="notes" value={form.notes} onChange={change} placeholder={t("Transaction ID, sender name, etc.")} />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>{t("Cancel")}</Button>
                    <Button type="submit" variant="primary" disabled={saving || maxAmount <= 0}>
                        {saving ? <Spinner size="sm" animation="border" /> : t("Send for approval")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
